import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { AdminBanner, CreateBannerArgs, UpdateBannerArgs } from './bannerApiTypes';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// Self-service home-carousel banners. CRUD lives on the new (ECS) backend at
// /admin/banners; see new-backend/admin/src/controllers/bannerController.ts.
// Banners are stored in tenant.config.branding.banners (JSONB) and the end-user
// app reads a server-filtered/sorted subset.
export const bannerApiSlice = createApi({
    reducerPath: 'bannerApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['BannerList'],
    endpoints: (builder) => ({
        getBanners: builder.query<AdminBanner[], void>({
            query: () => ({
                url: '/banners',
                method: 'GET',
            }),
            providesTags: ['BannerList'],
        }),
        // POST /admin/banners — multipart: image file + scalar fields. The
        // backend appends a new banner to the JSONB array. 'BannerList' is
        // provided by getBanners above.
        createBanner: builder.mutation<AdminBanner, CreateBannerArgs>({
            query: ({ imageDe, imageEn, type, href, voucherCode, active, startsAt, endsAt, order }) => {
                const formData = new FormData();
                if (imageDe) formData.append('image_de', imageDe);
                if (imageEn) formData.append('image_en', imageEn);
                formData.append('type', type);
                if (href) formData.append('href', href);
                if (voucherCode) formData.append('voucherCode', voucherCode);
                formData.append('active', String(active));
                if (startsAt) formData.append('startsAt', startsAt);
                if (endsAt) formData.append('endsAt', endsAt);
                formData.append('order', String(order));
                return {
                    url: '/banners',
                    method: 'POST',
                    data: formData,
                };
            },
            invalidatesTags: ['BannerList'],
        }),
        // PATCH /admin/banners/:id — multipart. Only the provided fields change
        // server-side, so we append exactly the keys that are defined.
        updateBanner: builder.mutation<AdminBanner, UpdateBannerArgs>({
            query: ({
                id,
                imageDe,
                imageEn,
                removeDe,
                removeEn,
                type,
                href,
                voucherCode,
                active,
                startsAt,
                endsAt,
                order,
            }) => {
                const formData = new FormData();
                if (imageDe) formData.append('image_de', imageDe);
                if (imageEn) formData.append('image_en', imageEn);
                if (removeDe) formData.append('remove_de', 'true');
                if (removeEn) formData.append('remove_en', 'true');
                if (type !== undefined) formData.append('type', type);
                if (href !== undefined) formData.append('href', href);
                if (voucherCode !== undefined) formData.append('voucherCode', voucherCode);
                if (active !== undefined) formData.append('active', String(active));
                if (startsAt !== undefined) formData.append('startsAt', startsAt);
                if (endsAt !== undefined) formData.append('endsAt', endsAt);
                if (order !== undefined) formData.append('order', String(order));
                return {
                    url: `/banners/${id}`,
                    method: 'PATCH',
                    data: formData,
                };
            },
            invalidatesTags: ['BannerList'],
        }),
        deleteBanner: builder.mutation<void, string>({
            query: (id) => ({
                url: `/banners/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['BannerList'],
        }),
    }),
});

export const {
    useGetBannersQuery,
    useCreateBannerMutation,
    useUpdateBannerMutation,
    useDeleteBannerMutation,
} = bannerApiSlice;
